import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AppSelectors } from 'app/shared/selectors';
import { StyledFlex, StyledInput } from './StyledComponents';

const VerificationCodeValidator = ({
    clearErrors,
    codeLength,
    onEnterKeyPress,
    onComplete,
}) => {
    const isMobile = useSelector(state => AppSelectors.makeIsMobile(state));
    const inputRefs = useRef([]);
    const [verificationCode, setVerificationCode] = useState(new Array(codeLength).fill(''));

    useEffect(() => {
        inputRefs.current[0].focus();
    }, []);

    const validateInput = (value) => {
        const numericValue = parseInt(value, 10);
        if ((numericValue >= 0 && numericValue <= 9) || value === '') {
            return true;
        }
        return false;
    };

    const isVerificationCodeComplete = (inputtedCode) => {
        const emptyValues = inputtedCode.filter(num => num === '');
        return emptyValues.length === 0;
    };

    const handleDelete = (index) => {
        const verificationCodeCopy = [...verificationCode];
        verificationCodeCopy[index] = '';
        setVerificationCode(verificationCodeCopy);

        if (index - 1 >= 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleOnEnterKeyPress = (key) => {
        const isComplete = isVerificationCodeComplete(verificationCode);
        if (isComplete) {
            onComplete(verificationCode.join(''));
            if (key === 'Enter') {
                onEnterKeyPress();
            }
        }
    };

    const getKey = e => new Promise((resolve) => {
        const { target, key: pKey } = e;

        setTimeout(() => {
            // on some devices the key value is returned as "Unidentified"
            const key = pKey === 'Unidentified' ? target.value : pKey;

            resolve(key);
        }, 10);
    });

    const handleKeyDown = async (e, index) => {
        // onKeyDown fires before onChange
        // get key asynchronously in order to get changed values
        const key = await getKey(e);

        clearErrors('');

        if (key === 'Backspace') {
            handleDelete(index);
            return;
        }

        if (key === 'Enter') {
            handleOnEnterKeyPress(key);
        }

        if (validateInput(key)) {
            const verificationCodeCopy = [...verificationCode];
            verificationCodeCopy[index] = key;

            const isLastInputField = index === verificationCodeCopy.length - 1;
            const isComplete = isVerificationCodeComplete(verificationCodeCopy);

            if (isComplete) {
                // still need to set focus when
                if (!isLastInputField && key !== 'Backspace') {
                    inputRefs.current[index + 1].focus();
                }

                onComplete(verificationCodeCopy.join(''));
                return;
            }

            if (key !== 'Backspace') {
                inputRefs.current[index + 1].focus();
            }
        }
    };

    const handleChange = (e, index) => {
        const key = e.target.value;

        if (validateInput(key)) {
            const verificationCodeCopy = [...verificationCode];
            verificationCodeCopy[index] = key;

            setVerificationCode(verificationCodeCopy);
        }
    };

    const handleOnFocus = (e) => {
        e.target.select(e);
    };

    return (
        <StyledFlex display="flex">
            {verificationCode.map((value, i) => (
                <StyledInput
                    ref={(el) => { inputRefs.current[i] = el; }}
                    key={`verification-code-${i}`}
                    maxLength="1"
                    value={value}
                    onKeyDown={e => handleKeyDown(e, i)}
                    onChange={e => handleChange(e, i)}
                    onFocus={handleOnFocus}
                    type={isMobile ? 'number' : 'text'}
                />
            ))}
        </StyledFlex>
    );
};

export { VerificationCodeValidator };
export default VerificationCodeValidator;
